import { ICONS } from '@ui/icons/types';
import NumberField from './number-field';
import BoxModelControl from './box-model-control';
import { useBoxModelStyle, type BoxModelValues } from './use-box-model-style';

const NO_RESOLVED_VALUES: BoxModelValues = [undefined, undefined, undefined, undefined];

interface BorderRadiusControlProps {
  label?: string;
  blockId: string;
  style: Record<string, unknown>;
  onUpdateStyle: (key: string, value: unknown) => void;
  /** DOM-resolved fallback corners [topLeft, topRight, bottomRight, bottomLeft], for a fresh block */
  resolvedValues?: BoxModelValues;
  /** adds the expand toggle revealing 4 independent corner fields, matching Padding/Margin. Defaults
   * to a single uniform field (Logo/Image's simpler needs) when omitted. */
  expandable?: boolean;
}

// shared "Rounded corners" / "Corner radius" field, backed by the same useBoxModelStyle logic
// Padding/Margin use - a single uniform value by default, or a full 4-corner BoxModelControl
// section when `expandable` is set (BlockStylePanel's Header/Paragraph corner radius).
export default function BorderRadiusControl({
  label = 'Rounded corners',
  blockId,
  style,
  onUpdateStyle,
  resolvedValues = NO_RESOLVED_VALUES,
  expandable,
}: BorderRadiusControlProps) {
  const corner = useBoxModelStyle({
    blockId,
    styleValue: style.borderRadius,
    resolvedValues,
    styleKey: 'borderRadius',
    uniformity: 'all',
    onUpdateStyle,
  });

  const uniformField = (
    <NumberField
      label={label}
      icon={ICONS.ROUNDED_CORNER}
      allowNegative={false}
      min={0}
      max={100}
      value={corner.values[0]}
      onChange={corner.setUniformValue}
    />
  );

  if (!expandable) return uniformField;

  return (
    <BoxModelControl
      sectionLabel={label}
      expanded={corner.expanded}
      onToggleExpand={() => corner.setExpanded((prev) => !prev)}
      toggleIcon={ICONS.ROUNDED_CORNER}
      toggleLabel="corner radius controls"
      values={corner.values}
      onChangeSlot={corner.setSlot}
      fields={[
        { slot: 0, icon: ICONS.CORNER_TOP_LEFT, ariaLabel: 'Top-left corner radius' },
        { slot: 1, icon: ICONS.CORNER_TOP_RIGHT, ariaLabel: 'Top-right corner radius' },
        { slot: 3, icon: ICONS.CORNER_BOTTOM_LEFT, ariaLabel: 'Bottom-left corner radius' },
        { slot: 2, icon: ICONS.CORNER_BOTTOM_RIGHT, ariaLabel: 'Bottom-right corner radius' },
      ]}
      renderCollapsed={() => uniformField}
    />
  );
}
